import React, { useMemo } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
    faCircleExclamation,
    faChartLine,
    faCircleDollar,
} from "@fortawesome/pro-regular-svg-icons";
import { useAuth, Roles } from "../../providers/AuthContext";
import { formatSwedishTime } from "../../helpers/DateTime";
import Tooltip from "../Tooltip";
import {
    getCustomerDisplayName,
    shouldShowOrganizationName,
} from "../../helpers/CustomerDisplay";
import DataTable from "../DataTable";
import { getStatusColorStyle } from "../../helpers/StatusMapper";
import { useTranslation } from "react-i18next";

const parseDate = (value) => {
    if (!value) {
        return null;
    }

    const date = new Date(value);
    return Number.isFinite(date.getTime()) ? date : null;
};

const durationToSeconds = (value) => {
    if (!value || typeof value !== "string") {
        return 0;
    }

    const parts = value.split(":").map((part) => parseInt(part, 10));
    if (parts.length !== 3 || parts.some((part) => !Number.isFinite(part))) {
        return 0;
    }

    return parts[0] * 3600 + parts[1] * 60 + parts[2];
};

const getChargerName = (row) =>
    row?.chargingConnectorName || row?.chargingPointName || "-";

const getPaymentMethodBadgeClassName = (paymentMethodName) => {
    const normalized = paymentMethodName?.toLowerCase() ?? "";

    if (normalized === "invoice") {
        return "bg-primary-100 text-primary-700";
    }

    if (normalized === "card") {
        return "bg-secondary-100 text-secondary-700";
    }

    return "bg-mute-100 text-mute-700";
};

const ZERO_ENERGY_LABEL_TO_KEY = {
    Fault: "CHARGER_FAULTED",
    Offline: "CHARGER_UNAVAILABLE",
    Token: "INVALID_TOKEN",
    Timeout: "COMMAND_TIMEOUT",
    Rejected: "COMMAND_REJECTED",
    Handshake: "VEHICLE_HANDSHAKE_FAILED",
    Paused: "CHARGER_SUSPENDED_TIMEOUT",
    Blocked: "VEHICLE_BLOCKED_CHARGING",
    "No energy": "NO_ENERGY_DELIVERED",
    "No auth": "NO_AUTH_ATTEMPT",
    Autocharge: "AUTOCHARGE_ONLY",
    "Wrong booking token": "BOOKING_TOKEN_MISMATCH",
    Unknown: "UNKNOWN",
};

const resolveZeroEnergyKey = (value) => {
    if (!value) {
        return null;
    }

    const trimmed = String(value).trim();
    if (ZERO_ENERGY_LABEL_TO_KEY[trimmed]) {
        return ZERO_ENERGY_LABEL_TO_KEY[trimmed];
    }

    const asEnum = trimmed.toUpperCase().replace(/[\s-]+/g, "_");
    if (Object.values(ZERO_ENERGY_LABEL_TO_KEY).includes(asEnum)) {
        return asEnum;
    }

    return null;
};

const getZeroEnergyLabel = (value, t) => {
    const key = resolveZeroEnergyKey(value);
    if (!key) {
        return value;
    }

    return t(`session.zeroEnergy.reason.${key}`, { defaultValue: value });
};

const getZeroEnergyDescription = (reasonValue, description, t) => {
    const key = resolveZeroEnergyKey(reasonValue);
    if (!key) {
        return description || "";
    }

    return t(`session.zeroEnergy.description.${key}`, {
        defaultValue: description || "",
    });
};

const getPaymentMethodLabel = (paymentMethodName, t) => {
    const normalized = paymentMethodName?.toLowerCase() ?? "";

    if (normalized === "invoice") {
        return t("payment.Invoice");
    }

    if (normalized === "card") {
        return t("payment.Card");
    }

    if (normalized === "other" || normalized === "unknown") {
        return t("payment.Other");
    }

    return paymentMethodName;
};

const cardPaymentWarningKey = (status) => {
    const normalized = String(status ?? "").trim().toLowerCase();
    if (normalized === "partiallypaid") {
        return "session.paymentPartiallyPaid";
    }

    if (normalized === "failed" || normalized === "uncollectible") {
        return "session.paymentNotCollected";
    }

    return null;
};

function SessionsTable({
    dimensions,
    rowsPerTable = 10,
    page = 1,
    numberOfRows = 0,
    numberOfPages = 1,
    onRowsPerTableChange = () => { },
    setPage = () => { },
    onDetailsClick,
}) {
    const groups = useMemo(() => dimensions ?? {}, [dimensions]);
    const { t } = useTranslation();
    const { hasRole } = useAuth();
    const includeOrganization = shouldShowOrganizationName(hasRole);
    const showErrorColumn = !hasRole(Roles.Partner) && !hasRole(Roles.Customer);
    const groupKeys = Object.keys(groups);

    const columns = useMemo(
        () => {
            const baseColumns = [
                {
                    key: "startDateTime",
                    header: t("table.startTime"),
                    sortable: true,
                    headerClassName: "whitespace-nowrap",
                    cellClassName: "whitespace-nowrap",
                    getSortValue: (row) => parseDate(row?.startDateTime),
                    render: (row) => formatSwedishTime(row?.startDateTimeUtc, false),
                },
                {
                    key: "chargingSiteName",
                    header: t("entity.site"),
                    sortable: true,
                    headerClassName: "whitespace-nowrap",
                    getSortValue: (row) => row?.chargingSiteName ?? "",
                    render: (row) => row?.chargingSiteName || "-",
                },
                {
                    key: "chargingPointName",
                    header: t("entity.charger"),
                    sortable: true,
                    headerClassName: "whitespace-nowrap",
                    getSortValue: (row) => getChargerName(row),
                    render: (row) => getChargerName(row),
                },
                {
                    key: "customerName",
                    header: t("entity.customer"),
                    sortable: true,
                    headerClassName: "whitespace-nowrap",
                    getSortValue: (row) => getCustomerDisplayName(row, includeOrganization),
                    render: (row) => getCustomerDisplayName(row, includeOrganization),
                },
                {
                    key: "tokenName",
                    header: t("entity.token"),
                    sortable: true,
                    headerClassName: "whitespace-nowrap",
                    getSortValue: (row) => row?.tokenName ?? "",
                    render: (row) => row?.tokenName || "-",
                },
                {
                    key: "sessionDuration",
                    header: t("table.sessionDuration"),
                    sortable: true,
                    headerClassName: "whitespace-nowrap",
                    getSortValue: (row) => durationToSeconds(row?.sessionDuration),
                    render: (row) => row?.sessionDuration || t("common.active"),
                },
                {
                    key: "energyCharged",
                    header: t("table.energyUsed"),
                    sortable: true,
                    headerClassName: "whitespace-nowrap",
                    getSortValue: (row) => Number(row?.energyCharged ?? 0),
                    render: (row, index, { rowCount = 0 } = {}) => {
                        const zeroEnergyReason = row?.zeroEnergyReason
                            ? {
                                label: getZeroEnergyLabel(row.zeroEnergyReason, t),
                                description: getZeroEnergyDescription(
                                    row.zeroEnergyReason,
                                    row?.zeroEnergyDescription,
                                    t
                                ),
                            }
                            : null;
                        const zeroEnergyTooltipPosition =
                            index >= Math.max(rowCount - 2, 0)
                                ? "top-left"
                                : "bottom-left";

                        return (
                            <div className="flex items-center gap-2 leading-tight">
                                <span >
                                    {Number(row?.energyCharged ?? 0).toFixed(2)}
                                </span>
                                {zeroEnergyReason && (
                                    <Tooltip
                                        content={zeroEnergyReason.description}
                                        position={zeroEnergyTooltipPosition}
                                    >
                                        <span className="inline-flex w-fit cursor-help items-center rounded-full bg-warning-100 px-2 py-0.5 text-xs font-medium text-warning-700">
                                            {zeroEnergyReason.label}
                                        </span>
                                    </Tooltip>
                                )}
                            </div>
                        );
                    },
                },
                {
                    key: "amount",
                    header: t("common.amount"),
                    sortable: true,
                    headerClassName: "whitespace-nowrap",
                    getSortValue: (row) => Number(row?.amount ?? 0),
                    render: (row) => {
                        const cardPaymentWarning = cardPaymentWarningKey(row?.receiptPaymentStatus);

                        return (
                            <>
                                {Number(row?.amount ?? 0).toFixed(2)}
                                {row?.emspSessionFinalized === false && (
                                    <FontAwesomeIcon
                                        icon={faCircleDollar}
                                        size="lg"
                                        title={t("session.notFinalizedEmsp")}
                                        style={{ marginLeft: 4, ...getStatusColorStyle("ERROR") }}
                                    />
                                )}
                                {cardPaymentWarning && (
                                    <FontAwesomeIcon
                                        icon={faCircleDollar}
                                        size="lg"
                                        title={t(cardPaymentWarning)}
                                        style={{ marginLeft: 4, ...getStatusColorStyle("ERROR") }}
                                    />
                                )}
                            </>
                        );
                    },
                },
                {
                    key: "currencyCode",
                    header: t("common.currency"),
                    sortable: true,
                    headerClassName: "whitespace-nowrap",
                    getSortValue: (row) => row?.currencyCode ?? "",
                    render: (row) => row?.currencyCode || "SEK",
                },
                {
                    key: "paymentMethodName",
                    header: t("table.payment"),
                    sortable: true,
                    headerClassName: "whitespace-nowrap",

                    getSortValue: (row) => row?.paymentMethodName ?? "",
                    render: (row) =>
                        row?.paymentMethodName ? (
                            <span
                                className={`inline-flex rounded-full px-2.5 py-0.5 text-xs font-medium ${getPaymentMethodBadgeClassName(row.paymentMethodName)}`}
                            >
                                {getPaymentMethodLabel(row.paymentMethodName, t)}
                            </span>
                        ) : (
                            "-"
                        ),
                },
                {
                    key: "details",
                    header: t("common.details"),
                    sortable: false,
                    searchable: false,
                    headerClassName: "whitespace-nowrap",
                    cellClassName: "text-center",
                    render: (row) =>
                        row.chargingConnectorPublicId && row.endDateTimeUtc ? (
                            <button
                                onClick={() => onDetailsClick?.(row)}
                                title={t("session.showDetails")}
                                className="rounded hover:bg-primary-50 transition"
                                style={getStatusColorStyle("INFO")}
                            >
                                <FontAwesomeIcon icon={faChartLine} size="xl" />
                            </button>
                        ) : (
                            "-"
                        ),
                },
            ];

            if (showErrorColumn) {
                baseColumns.push({
                    key: "sessionHasErrorCode",
                    header: t("common.error"),
                    sortable: true,
                    searchable: false,
                    headerClassName: "whitespace-nowrap",
                    cellClassName: "text-center",
                    getSortValue: (row) => (row?.sessionHasErrorCode ? 1 : 0),
                    render: (row) =>
                        row?.sessionHasErrorCode ? (
                            <FontAwesomeIcon
                                icon={faCircleExclamation}
                                size="lg"
                                title={t("session.hasErrorCode")}
                                style={getStatusColorStyle("FAULTED")}
                            />
                        ) : (
                            ""
                        ),
                });
            }

            return baseColumns;
        },
        [onDetailsClick, showErrorColumn, includeOrganization, t]
    );

    const getHeaderColor = (index) => {
        if (groupKeys.length <= 1) {
            return "bg-mute-100";
        }

        if (index === 0) {
            return "bg-primary-100";
        }

        if (index === 1) {
            return "bg-secondary-100";
        }

        if (index === 2) {
            return "bg-warning-100";
        }

        if (index === 3) {
            return "bg-error-100";
        }

        return "bg-mute-100";
    };

    return (
        <>
            {groupKeys.map((key, index) => {
                const rows = Array.isArray(groups[key]) ? groups[key] : [];
                const columnsWithRowCount = columns.map((column) => {
                    if (column.key !== "energyCharged") {
                        return column;
                    }

                    return {
                        ...column,
                        render: (row, rowIndex, context) =>
                            column.render(row, rowIndex, {
                                ...context,
                                rowCount: rows.length,
                            }),
                    };
                });

                return (
                    <DataTable
                        key={key + index}
                        rows={rows}
                        columns={columnsWithRowCount}
                        defaultSortColumn="startDateTime"
                        defaultSortOrder="desc"
                        defaultRowsPerPage={rowsPerTable}
                        rowKey={(row, rowIndex) => row?.chargingSessionPublicId ?? rowIndex}
                        tableClassName="table-default"
                        containerClassName="overflow-x-auto w-full"
                        defaultHeaderClassName="align-middle whitespace-nowrap"
                        defaultCellClassName="align-middle"
                        headerRowClassName={getHeaderColor(index)}
                        externalPagination
                        externalPage={page}
                        externalRowsPerPage={rowsPerTable}
                        externalTotalRows={numberOfRows}
                        externalTotalPages={numberOfPages}
                        onExternalPageChange={setPage}
                        onExternalRowsPerPageChange={onRowsPerTableChange}
                        skipClientSearch
                        showPagination={index === groupKeys.length - 1}
                    />
                );
            })}
        </>
    );
}

export default SessionsTable;
